import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaTag,
  FaPercent,
  FaClock,
  FaBan,
  FaCalendarDay,
  FaCalendarWeek,
  FaCalendarAlt,
  FaTicketAlt,
  FaPercentage,
  FaChevronDown,
  FaEuroSign,
  FaShoppingCart,
  FaUsers,
  FaEdit,
  FaPlus,
  FaTrash,
} from "react-icons/fa";
import LoadingSpinner from "../../LoadingSpinner/LoadingSpinner";

function Coupons({
  couponsData,
  durationDiscounts,
  handleDurationDiscountUpdate,
  editingCoupon,
  setEditingCoupon,
  handleCouponEdit,
  creatingCoupon,
  handleCouponCreate,
  deletingCouponId,
  handleCouponDelete,
}) {
  return (
    <div className="space-y-8 animate-fadeIn">
      {/* Coupon Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-xl shadow-sm border border-blue-200 hover:shadow-md transition-shadow"
        >
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-blue-500/20 text-blue-600">
              <FaTag className="w-6 h-6" />
            </div>
            <div className="ml-4">
              <h3 className="text-sm font-medium text-blue-700">
                Active Coupons
              </h3>
              <p className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-blue-800 text-transparent bg-clip-text">
                {
                  couponsData.filter((c) => new Date(c.expiryDate) > new Date())
                    .length
                }
              </p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-gradient-to-br from-green-50 to-green-100 p-6 rounded-xl shadow-sm border border-green-200 hover:shadow-md transition-shadow"
        >
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-green-500/20 text-green-600">
              <FaPercent className="w-6 h-6" />
            </div>
            <div className="ml-4">
              <h3 className="text-sm font-medium text-green-700">Total Uses</h3>
              <p className="text-2xl font-bold bg-gradient-to-r from-green-600 to-green-800 text-transparent bg-clip-text">
                {couponsData.reduce((total, c) => total + c.usageCount, 0)}
              </p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-gradient-to-br from-amber-50 to-amber-100 p-6 rounded-xl shadow-sm border border-amber-200 hover:shadow-md transition-shadow"
        >
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-amber-500/20 text-amber-600">
              <FaClock className="w-6 h-6" />
            </div>
            <div className="ml-4">
              <h3 className="text-sm font-medium text-amber-700">
                Expiring Soon
              </h3>
              <p className="text-2xl font-bold bg-gradient-to-r from-amber-600 to-amber-800 text-transparent bg-clip-text">
                {
                  couponsData.filter((c) => {
                    const daysUntilExpiry = Math.ceil(
                      (new Date(c.expiryDate) - new Date()) /
                        (1000 * 60 * 60 * 24)
                    );
                    return daysUntilExpiry <= 7 && daysUntilExpiry > 0;
                  }).length
                }
              </p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-gradient-to-br from-red-50 to-red-100 p-6 rounded-xl shadow-sm border border-red-200 hover:shadow-md transition-shadow"
        >
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-red-500/20 text-red-600">
              <FaBan className="w-6 h-6" />
            </div>
            <div className="ml-4">
              <h3 className="text-sm font-medium text-red-700">Expired</h3>
              <p className="text-2xl font-bold bg-gradient-to-r from-red-600 to-red-800 text-transparent bg-clip-text">
                {
                  couponsData.filter(
                    (c) => new Date(c.expiryDate) <= new Date()
                  ).length
                }
              </p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Duration-based Discounts Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden transition-all hover:shadow-md"
      >
        <div className="border-b border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4">
          <h3 className="text-lg font-semibold text-gray-900">
            Duration-Based Discounts
          </h3>
          <p className="text-sm text-gray-500 mt-1">
            Configure automatic discounts based on rental duration
          </p>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                term: "shortTerm",
                label: "Short Term (1-3 days)",
                color: "blue",
                icon: <FaCalendarDay className="mr-2" />,
              },
              {
                term: "mediumTerm",
                label: "Medium Term (4-10 days)",
                color: "green",
                icon: <FaCalendarWeek className="mr-2" />,
              },
              {
                term: "longTerm",
                label: "Long Term (10+ days)",
                color: "purple",
                icon: <FaCalendarAlt className="mr-2" />,
              },
            ].map(({ term, label, color, icon }) => (
              <div
                key={term}
                className={`relative bg-${color}-50 p-4 rounded-lg border border-${color}-200 transition-all hover:shadow-sm`}
              >
                <label
                  className={`flex items-center text-sm font-medium text-${color}-700 mb-3`}
                >
                  {icon} {label}
                </label>
                <div className="flex items-center">
                  <div className="relative flex-1 mr-4">
                    <input
                      type="range"
                      min="0"
                      max="100"
                      step="5"
                      value={durationDiscounts[term].discount}
                      onChange={(e) =>
                        handleDurationDiscountUpdate(term, e.target.value)
                      }
                      className={`w-full h-2 bg-${color}-200 rounded-lg appearance-none cursor-pointer`}
                    />
                  </div>
                  <div className="w-16 relative">
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={durationDiscounts[term].discount}
                      onChange={(e) =>
                        handleDurationDiscountUpdate(term, e.target.value)
                      }
                      className={`block w-full rounded-lg border border-${color}-300 text-center focus:border-${color}-500 focus:ring-${color}-500 p-2`}
                    />
                    <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                      <span className="text-gray-500">%</span>
                    </div>
                  </div>
                </div>
                <div className="mt-2 text-center">
                  <span className={`text-${color}-600 text-sm font-medium`}>
                    {durationDiscounts[term].discount}% discount
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </motion.div>

      {/* Coupon Creation/Edit Form */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden transition-all hover:shadow-md"
      >
        <div className="border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <FaTicketAlt className="mr-2 text-indigo-600" />
            {editingCoupon ? "Edit Coupon" : "Create New Coupon"}
          </h3>
          <p className="text-sm text-gray-500 mt-1">
            {editingCoupon
              ? "Modify existing coupon details"
              : "Create a new discount coupon for your customers"}
          </p>
        </div>
        <div className="p-6">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const formData = new FormData(e.target);
              const couponData = {
                code: formData.get("code").toUpperCase(),
                discountType: formData.get("discountType"),
                discountValue: Number(formData.get("discountValue")),
                minOrderAmount: Number(formData.get("minOrderAmount")),
                expiryDate: formData.get("expiryDate"),
                maxUses: Number(formData.get("maxUses")),
              };

              if (editingCoupon) {
                handleCouponEdit(couponData);
              } else {
                handleCouponCreate(couponData);
              }
            }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            <div className="space-y-2">
              <label className="flex items-center text-sm font-medium text-gray-700">
                <FaTag className="mr-2 text-blue-500" /> Coupon Code
              </label>
              <div className="relative">
                <input
                  type="text"
                  name="code"
                  required
                  defaultValue={editingCoupon?.code || ""}
                  placeholder="e.g., SUMMER2025"
                  className="block w-full rounded-lg border-0 bg-gray-50 px-4 py-3 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 transition-all uppercase"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="flex items-center text-sm font-medium text-gray-700">
                <FaPercentage className="mr-2 text-green-500" /> Discount Type
              </label>
              <div className="relative">
                <select
                  name="discountType"
                  required
                  defaultValue={editingCoupon?.discountType || "percentage"}
                  className="block w-full rounded-lg border-0 bg-gray-50 px-4 py-3 text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-blue-600 transition-all appearance-none"
                >
                  <option value="percentage">Percentage (%)</option>
                  <option value="fixed">Fixed Amount (€)</option>
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-500">
                  <FaChevronDown className="h-4 w-4" />
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <label className="flex items-center text-sm font-medium text-gray-700">
                <FaEuroSign className="mr-2 text-amber-500" /> Discount Value
              </label>
              <div className="relative">
                <input
                  type="number"
                  name="discountValue"
                  required
                  defaultValue={editingCoupon?.discountValue || ""}
                  placeholder="Enter amount or percentage"
                  className="block w-full rounded-lg border-0 bg-gray-50 px-4 py-3 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 transition-all"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="flex items-center text-sm font-medium text-gray-700">
                <FaShoppingCart className="mr-2 text-purple-500" /> Minimum
                Order Amount
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <span className="text-gray-500">€</span>
                </div>
                <input
                  type="number"
                  name="minOrderAmount"
                  required
                  defaultValue={editingCoupon?.minOrderAmount || ""}
                  placeholder="Minimum order value"
                  className="block w-full rounded-lg border-0 bg-gray-50 pl-8 pr-4 py-3 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 transition-all"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="flex items-center text-sm font-medium text-gray-700">
                <FaCalendarAlt className="mr-2 text-red-500" /> Expiry Date
              </label>
              <div className="relative">
                <input
                  type="date"
                  name="expiryDate"
                  required
                  defaultValue={
                    editingCoupon?.expiryDate
                      ? new Date(editingCoupon.expiryDate)
                          .toISOString()
                          .split("T")[0]
                      : ""
                  }
                  className="block w-full rounded-lg border-0 bg-gray-50 px-4 py-3 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 transition-all"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="flex items-center text-sm font-medium text-gray-700">
                <FaUsers className="mr-2 text-blue-500" /> Maximum Uses
              </label>
              <div className="relative">
                <input
                  type="number"
                  name="maxUses"
                  required
                  defaultValue={editingCoupon?.maxUses || ""}
                  placeholder="Maximum number of uses"
                  className="block w-full rounded-lg border-0 bg-gray-50 px-4 py-3 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 transition-all"
                />
              </div>
            </div>

            <div className="md:col-span-2 lg:col-span-3 flex gap-4">
              {editingCoupon && (
                <button
                  type="button"
                  onClick={() => setEditingCoupon(null)}
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-lg shadow-sm text-base font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all"
                >
                  Cancel
                </button>
              )}
              <button
                type="submit"
                disabled={creatingCoupon}
                className="flex-1 flex justify-center items-center px-4 py-3 border border-transparent rounded-lg shadow-sm text-base font-medium text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                {creatingCoupon ? (
                  <>
                    <LoadingSpinner size="sm" />
                    <span className="ml-2">
                      {editingCoupon ? "Updating..." : "Creating..."}
                    </span>
                  </>
                ) : (
                  <span className="flex items-center">
                    {editingCoupon ? (
                      <>
                        <FaEdit className="mr-2" /> Update Coupon
                      </>
                    ) : (
                      <>
                        <FaPlus className="mr-2" /> Create Coupon
                      </>
                    )}
                  </span>
                )}
              </button>
            </div>
          </form>
        </div>
      </motion.div>

      {/* Active Coupons Grid */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-4"
      >
        <h3 className="text-xl font-bold text-gray-800 flex items-center">
          <FaTicketAlt className="mr-2 text-indigo-600" />
          Active Coupons
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <AnimatePresence mode="wait">
            {couponsData.map((coupon, index) => {
              const isExpired = new Date(coupon.expiryDate) <= new Date();
              const usagePercentage =
                (coupon.usageCount / coupon.maxUses) * 100;
              const expiryDate = new Date(coupon.expiryDate);
              const daysRemaining = Math.ceil(
                (expiryDate - new Date()) / (1000 * 60 * 60 * 24)
              );

              // Determine status color
              let statusColor = "green";
              if (isExpired) {
                statusColor = "red";
              } else if (daysRemaining <= 3) {
                statusColor = "red";
              } else if (daysRemaining <= 7) {
                statusColor = "amber";
              }

              return (
                <motion.div
                  key={coupon._id}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95, y: 20 }}
                  transition={{ delay: index * 0.05 }}
                  className={`bg-white rounded-xl shadow-sm border-2 ${
                    isExpired ? "border-red-300" : "border-gray-100"
                  } overflow-hidden hover:shadow-md transition-all duration-300 relative`}
                >
                  <div
                    className={`h-2 w-full ${
                      isExpired
                        ? "bg-red-500"
                        : usagePercentage > 75
                        ? "bg-amber-500"
                        : "bg-green-500"
                    }`}
                  ></div>

                  <div className="p-6">
                    <div className="flex justify-between items-start mb-4">
                      <div className="space-y-3">
                        <div className="flex items-center space-x-2">
                          <span className="px-3 py-1 rounded-full text-sm font-bold bg-indigo-100 text-indigo-800">
                            {coupon.code}
                          </span>
                          {isExpired && (
                            <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 animate-pulse">
                              EXPIRED
                            </span>
                          )}
                        </div>
                        <div className="space-y-1">
                          <p className="text-2xl font-bold text-gray-900 flex items-center">
                            {coupon.discountType === "percentage" ? (
                              <>
                                <span className="bg-gradient-to-r from-indigo-600 to-indigo-800 text-transparent bg-clip-text">
                                  {coupon.discountValue}%
                                </span>
                              </>
                            ) : (
                              <>
                                <span className="bg-gradient-to-r from-blue-600 to-indigo-800 text-transparent bg-clip-text">
                                  €{coupon.discountValue}
                                </span>
                              </>
                            )}
                            <span className="text-sm font-normal text-gray-500 ml-1">
                              discount
                            </span>
                          </p>
                          <p className="text-sm text-gray-600 flex items-center">
                            <FaShoppingCart className="w-3.5 h-3.5 mr-1.5 text-gray-400" />
                            Minimum order:{" "}
                            <span className="font-medium text-gray-800 ml-1">
                              €{coupon.minOrderAmount}
                            </span>
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => setEditingCoupon(coupon)}
                          className="p-2 rounded-full hover:bg-blue-50 text-gray-400 hover:text-blue-500 focus:outline-none transition-all"
                        >
                          <FaEdit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleCouponDelete(coupon._id)}
                          disabled={deletingCouponId === coupon._id}
                          className="p-2 rounded-full hover:bg-red-50 text-gray-400 hover:text-red-500 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                        >
                          {deletingCouponId === coupon._id ? (
                            <LoadingSpinner size="sm" />
                          ) : (
                            <FaTrash className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </div>

                    <div className="px-6 pb-5 pt-3">
                      {/* Usage indicator with animation */}
                      <div className="mb-2 flex justify-between items-center text-xs text-gray-500">
                        <span>Usage</span>
                        <span className="font-medium">
                          {coupon.usageCount} of {coupon.maxUses}
                        </span>
                      </div>
                      <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
                        <div
                          className={`h-2 rounded-full ${
                            isExpired
                              ? "bg-gray-300"
                              : usagePercentage > 75
                              ? "bg-red-500"
                              : usagePercentage > 50
                              ? "bg-amber-500"
                              : "bg-green-500"
                          } transition-all duration-500 ease-out`}
                          style={{
                            width: `${usagePercentage}%`,
                          }}
                        />
                      </div>

                      {/* Expiry countdown */}
                      <div className="mt-4 flex justify-between items-center text-xs">
                        <div
                          className={`flex items-center ${
                            isExpired
                              ? "text-red-500"
                              : daysRemaining <= 3
                              ? "text-red-500"
                              : daysRemaining <= 7
                              ? "text-amber-500"
                              : "text-green-600"
                          }`}
                        >
                          <FaCalendarAlt className="w-3.5 h-3.5 mr-1.5" />
                          {isExpired ? (
                            <span className="font-medium">
                              Expired on {expiryDate.toLocaleDateString()}
                            </span>
                          ) : (
                            <span className="font-medium">
                              {daysRemaining === 0
                                ? "Expires today"
                                : daysRemaining === 1
                                ? "Expires tomorrow"
                                : `${daysRemaining} days remaining`}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
}

export default Coupons;
